<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import { capitalize } from 'lodash';

import { getWeekDay } from '@/data/week_days';
import { LISTING_SEARCH } from '@/composables/useListingSearch';

export default {
  name: 'ProposalFiltersBy',
  inject: {
    listingSearch: { from: LISTING_SEARCH, default: null },
  },
  data: () => ({
    storageKey: 'filters',
    isOpen: false,
  }),
  computed: {
    ...mapState({
      filters: (state) => state.proposal.filters,
      filtersNames: (state) => state.proposal.filtersNames,
      filterSearched: (state) => state.proposal.filterSearched,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      getAssigneeNameBySlug: 'contract/getAssigneeNameBySlug',
    }),
    visibleFilters() {
      return this.filterSearched ? this.filters : {};
    },
    awaitingSearch() {
      return Boolean(this.listingSearch?.awaitingSearch);
    },
    hasFilters() {
      return Object
        .values(this.visibleFilters)
        .some((val) => Boolean(val));
    },
    usedFilters() {
      const keys = Object.keys(this.visibleFilters);
      const toExclude = [
        'begin_date',
        'end_date',
      ];
      const toHide = [
        'responsible_id',
        'promoter_id',
        'supplier_id',
      ];

      const data = keys.reduce((acc, key) => {
        if (toExclude.includes(key)) {
          return acc;
        }

        if (this.visibleFilters[key]) {
          if (
            typeof this.visibleFilters[key] === 'boolean'
            || toHide.includes(key)
          ) {
            acc[this.filtersNames[key]] = { value: '', key };
          } else {
            acc[this.filtersNames[key]] = {
              value: this.visibleFilters[key],
              key,
            };
          }
        }

        return acc;
      }, {});

      if (
        this.visibleFilters.begin_date
        && keys.includes('begin_date')
        && keys.includes('end_date')
      ) {
        data['Período'] = {
          value: `${this.visibleFilters.begin_date} - ${this.visibleFilters.end_date}`,
          key: 'begin_date',
        };
      }

      return data;
    },
  },
  methods: {
    ...mapActions({
      updateFilters: 'proposal/UPDATE_FILTERS',
    }),
    /** clean storage filters directly */
    cleanStorageFilter(key) {
      if (key === 'begin_date') {
        this.updateFilters({ begin_date: '', end_date: '' });
      } else {
        this.updateFilters({
          [key]: typeof this.filters[key] === 'boolean' ? false : '',
        });
      }

      sessionStorage.removeItem(this.storageKey);
      this.$emit('paginate');
    },
    resolveValueLabel({ value, key }) {
      if (key === 'week_day') return getWeekDay(value);
      if (key === 'contract_status') return capitalize(value.split('_').join(' '));
      if (key === 'assignee') return this.getAssigneeNameBySlug(value) || value;
      return value;
    },
  },
};
</script>

<template>
  <div class="proposal-filters-by">
    <section
      v-if="hasFilters"
      class="hasFilters"
      :class="{ isMobile }"
    >
      <section
        class="is-flex is-align-items-center is-justify-content-space-between"
      >
        <span>
          <strong>{{ awaitingSearch ? 'Filtros da última busca' : 'Filtros aplicados' }}</strong>
        </span>
        <slot name="actions" />
      </section>
      <ul class="chips">
        <li
          v-for="(item, key, index) in usedFilters"
          :key="index"
          class="chips__item"
        >
          <span class="chips__head">{{ key }}</span>
          <span v-if="item.value">
            {{ resolveValueLabel(item) }}
          </span>
          <button
            class="chips__close"
            @click="cleanStorageFilter(item.key)"
          >
            <i class="fas fa-times" />
          </button>
        </li>
      </ul>
    </section>
    <template v-else>
      <section
        class="is-flex is-align-items-center is-justify-content-space-between is-full"
      >
        <span><i>Sem filtros</i></span>
        <slot name="actions" />
      </section>
    </template>
    <p
      v-if="awaitingSearch"
      class="notification is-info is-light py-2 px-3 mb-0 listing-search-notice"
      role="status"
    >
      Clique em Buscar para ver as propostas.
    </p>
  </div>
</template>

<style lang="scss" scoped>
.proposal-filters-by {
  display: flex;
  width: 100%;
  gap: .5rem;

  flex-direction: column;
  flex-wrap: wrap;

  .listing-search-notice {
    width: 100%;
  }

  span {
    white-space: nowrap;
    font-style: normal;
  }

  .hasFilters {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  @media (min-width: $breakpoint-desktop) {
    flex-direction: row;
    align-items: center;
  }
}

.chips {
  $gap: .5rem;

  display: flex;
  gap: $gap;
  overflow-x: auto;
  max-width: calc(100vw - 80px);

  &__item {
    display: flex;
    border: 1px solid $blue-700;
    border-radius: 8px;
    align-items: center;
    overflow: hidden;

    color: $blue-700;

    flex-wrap: nowrap;
    flex-shrink: 0;
  }

  &__head {
    padding: .25rem .5rem;

    font-weight: 700;

    ~ span {
      padding: #{$gap / 2} #{$gap};
      padding-left: 0;
    }
  }

  &__close {
    width: 2rem;
    height: 100%;
    background-color: rgba(#000, 0);
    border: none;
    padding: 0;

    color: $blue-700;

    cursor: pointer;

    &:hover,
    &:active {
      background-color: $blue-700;
      color: #fff;
    }
  }
}

.is-full {
  width: 100%;
}
</style>
